/**
 * Context for the InterruptCard compound.
 *
 * Holds the interrupt payload, the consumer-supplied submit callback,
 * and the local UI state (which response type the user has selected,
 * the response textarea value, submit-in-flight, error). Children
 * (Header, Approve, Respond, etc.) read from this context instead of
 * being prop-drilled.
 */
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import type { HumanInterrupt, HumanResponse } from "../humanInterrupt";

export interface InterruptCardContextValue {
  interrupt: HumanInterrupt;
  /** Currently-selected response type, or null until the user picks. */
  selectedType: HumanResponse["type"] | null;
  setSelectedType: (t: HumanResponse["type"] | null) => void;
  /** Free-text content for `respond` submissions. */
  responseText: string;
  setResponseText: (s: string) => void;
  /** True while the consumer's `onResponse` promise is in flight. */
  submitting: boolean;
  /** Last submit error message, if any. */
  error: string | null;
  /**
   * Submit a response. Resets local state on success; surfaces errors via
   * `error` (does NOT re-throw — buttons fire-and-forget via void submit()
   * and re-throwing would just become an unhandled rejection). Consumers
   * who need to react to success/failure should read `submitting` + `error`
   * from context, not await this promise's rejection.
   */
  submit: (response: HumanResponse) => Promise<void>;
}

const InterruptCardContext = createContext<InterruptCardContextValue | null>(null);

export function useInterruptCard(): InterruptCardContextValue {
  const ctx = useContext(InterruptCardContext);
  if (!ctx) {
    throw new Error(
      "InterruptCard parts must be rendered inside <InterruptCard>. " +
        "Wrap them in a parent that provides the interrupt + onResponse."
    );
  }
  return ctx;
}

export interface InterruptCardProviderProps {
  interrupt: HumanInterrupt;
  /**
   * Consumer-supplied transport. The compound just builds the
   * HumanResponse; the consumer decides how to ship it (typically a
   * `client.runs.stream(threadId, graphId, { command: { resume: response } })`
   * call against the LangGraph SDK).
   */
  onResponse: (response: HumanResponse) => Promise<void>;
  children: ReactNode;
}

export function InterruptCardProvider({
  interrupt,
  onResponse,
  children,
}: InterruptCardProviderProps) {
  const [selectedType, setSelectedType] = useState<HumanResponse["type"] | null>(null);
  const [responseText, setResponseText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Pin the latest onResponse in a ref so submit() never closes over a
  // stale callback even if the consumer passes a fresh function each render.
  const onResponseRef = useRef(onResponse);
  onResponseRef.current = onResponse;

  const submit = useCallback(async (response: HumanResponse) => {
    setSubmitting(true);
    setError(null);
    try {
      await onResponseRef.current(response);
      setSelectedType(null);
      setResponseText("");
    } catch (e) {
      // Surface the error via context (the inline ErrorBanner reads it).
      // We don't re-throw — buttons fire-and-forget via void submit(), and
      // re-throwing here would just become an unhandled rejection.
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSubmitting(false);
    }
  }, []);

  const value = useMemo<InterruptCardContextValue>(
    () => ({
      interrupt,
      selectedType,
      setSelectedType,
      responseText,
      setResponseText,
      submitting,
      error,
      submit,
    }),
    [interrupt, selectedType, responseText, submitting, error, submit]
  );

  return (
    <InterruptCardContext.Provider value={value}>
      {children}
    </InterruptCardContext.Provider>
  );
}
